/**
 * Statussen waarmee een traject als afgelopen geldt, per soort entiteit.
 * Eén bron, zodat elke berekening dezelfde eindstreep gebruikt.
 */
const TRAJECT_END_STATUSES = {
    adress: ['completed'],
    persona: ['outflow'],
}

/** Leest zowel een Firestore-tijdstempel als een kale milliseconde-waarde. */
const toMillis = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value.toMillis === 'function') return value.toMillis()
    if (typeof value.toDate === 'function') return value.toDate().getTime()
    return null
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Hele dagen tussen twee momenten, naar beneden afgerond. Geeft `null` terug
 * zodra een van beide ontbreekt of het einde vóór het begin ligt — dan valt er
 * geen duur te noemen.
 *
 * @param {number|null|undefined} fromMs
 * @param {number|null|undefined} toMs
 * @returns {number|null}
 */
const daysBetween = (fromMs, toMs) => {
    if (fromMs === null || fromMs === undefined) return null
    if (toMs === null || toMs === undefined) return null
    if (toMs < fromMs) return null
    return Math.floor((toMs - fromMs) / DAY_MS)
}

/**
 * Het startmoment van een traject: de expliciet meegegeven start, anders de
 * oudste vastgelegde statuswissel.
 *
 * @param {Array<{ChangedAt?: *}>} entries
 * @param {number} [startedAt]
 * @returns {number|null}
 */
const trajectStart = (entries, startedAt) => {
    if (startedAt !== undefined && startedAt !== null) return startedAt

    const times = (entries || []).map((entry) => toMillis(entry.ChangedAt)).filter((ms) => ms !== null)
    if (times.length === 0) return null
    return Math.min(...times)
}

/**
 * De doorlooptijd van één traject: van de eerste vastgelegde status tot de
 * laatste afsluitende status.
 *
 * De LAATSTE afsluiting telt, niet de eerste: een traject dat heropend werd en
 * daarna opnieuw is afgerond, duurde tot die tweede afronding.
 *
 * @param {Array<{FromStatus?: string, ToStatus?: string, ChangedAt?: *}>} entries
 *        Statushistorie van één huishouden of deelnemer, in willekeurige volgorde
 * @param {object} options
 * @param {Array<string>} options.endStatuses - Statussen die het traject afsluiten
 * @param {number} [options.startedAt] - Startmoment in ms als de historie geen beginregel heeft
 * @returns {{startedAt: number, endedAt: number, days: number}|null}
 *          `null` zolang het traject niet is afgesloten
 */
const computeTrajectDuration = (entries, { endStatuses, startedAt } = {}) => {
    const closing = endStatuses || []

    const timed = (entries || [])
        .map((entry) => ({ ...entry, ms: toMillis(entry.ChangedAt) }))
        .filter((entry) => entry.ms !== null)
        .sort((a, b) => a.ms - b.ms)

    if (timed.length === 0) return null

    const start = startedAt !== undefined && startedAt !== null ? startedAt : timed[0].ms

    const lastClosing = [...timed].reverse().find((entry) => closing.includes(entry.ToStatus))
    if (!lastClosing) return null
    if (lastClosing.ms < start) return null

    return {
        startedAt: start,
        endedAt: lastClosing.ms,
        days: daysBetween(start, lastClosing.ms),
    }
}

export default computeTrajectDuration
export { TRAJECT_END_STATUSES, daysBetween, trajectStart }
